import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FlaskIcon,
  HeadphonesIcon,
  InboxIcon,
  SendIcon,
  SettingsIcon,
} from "./components/icons";

type NavItem = {
  href: string;
  label: string;
  icon: typeof HeadphonesIcon;
  matches: (loc: string) => boolean;
};

type NavGroup = {
  label?: string;
  items: NavItem[];
};

// IA: Agent Desk (live human work) | Observe (passive monitoring of automated
// calls) | Evaluate (quality + testing) | Settings. Group labels show in
// expanded mode only; collapsed mode uses dividers between groups.
const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      {
        href: "/agent-desk",
        label: "Agent Desk",
        icon: HeadphonesIcon,
        matches: (l) =>
          (l.startsWith("/agent-desk") && !l.startsWith("/agent-desk/simulator")) ||
          l.startsWith("/copilot"),
      },
    ],
  },
  {
    label: "Observe",
    items: [
      {
        href: "/inbound",
        label: "Inbound",
        icon: InboxIcon,
        matches: (l) => l.startsWith("/inbound"),
      },
      {
        href: "/outbound",
        label: "Outbound",
        icon: SendIcon,
        matches: (l) => l.startsWith("/outbound"),
      },
    ],
  },
  {
    label: "Evaluate",
    items: [
      {
        href: "/scenarios",
        label: "Scenarios",
        icon: FlaskIcon,
        matches: (l) => l.startsWith("/scenarios") || l.startsWith("/test-agent"),
      },
      {
        href: "/agent-desk/simulator",
        label: "Advisor sim",
        icon: FlaskIcon,
        matches: (l) => l.startsWith("/agent-desk/simulator"),
      },
    ],
  },
  {
    items: [
      {
        href: "/settings",
        label: "Settings",
        icon: SettingsIcon,
        matches: (l) => l.startsWith("/settings") || l.startsWith("/flows"),
      },
    ],
  },
];

const COLLAPSED_KEY = "appShell:railCollapsed";

/**
 * Slim left rail rendered alongside every page. Collapsible (52px icons-only
 * by default, 220px with labels when expanded). State persists in
 * localStorage.
 *
 * Pages with their own sub-navigation (today: Settings) pass it via the
 * `secondaryNav` prop. The shell renders it as a sibling column right of
 * the rail, so the sub-nav lives at chrome level rather than nested inside
 * the page's own grid.
 */
export function AppShell({
  children,
  secondaryNav,
}: {
  children: ReactNode;
  secondaryNav?: ReactNode;
}) {
  const [location] = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) !== "0";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* private mode — toggle still works for the session */
    }
  }, [collapsed]);

  return (
    <div
      id="app-shell"
      className={`app-shell ${collapsed ? "is-collapsed" : "is-expanded"}`}
    >
      <nav id="app-rail" className="app-rail" aria-label="Primary">
        <div className="app-rail-head">
          <Link id="app-rail-brand" href="/" className="app-brand-chip" title="Home">
            AC
          </Link>
          <button
            id="app-rail-toggle"
            type="button"
            className="app-rail-toggle"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <ChevronRightIcon size={16} /> : <ChevronLeftIcon size={16} />}
          </button>
        </div>

        <div className="app-rail-groups">
          {NAV_GROUPS.map((group, gi) => (
            <div
              key={group.label ?? `g-${gi}`}
              className="app-rail-group"
              data-group={group.label ?? ""}
            >
              {group.label && (
                <div className="app-rail-group-label">{group.label}</div>
              )}
              {group.items.map((item) => {
                const active = item.matches(location);
                const Icon = item.icon;
                const slug = item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <Link
                    key={item.href}
                    id={`app-rail-link-${slug}`}
                    href={item.href}
                    className={`app-rail-link ${active ? "is-active" : ""}`}
                    title={item.label}
                  >
                    <span className="app-rail-icon" aria-hidden>
                      <Icon size={18} />
                    </span>
                    <span className="app-rail-label">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </nav>

      {secondaryNav && (
        <aside
          id="app-secondary-nav"
          className="app-secondary-nav"
          aria-label="Section navigation"
        >
          {secondaryNav}
        </aside>
      )}

      <div id="app-shell-body" className="app-shell-body">
        {children}
      </div>
    </div>
  );
}
